import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Image as ImageIcon, Sparkles, Check, Loader, X, Edit, Copy, Share2, AlertCircle } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IAImageAds = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [generatedAd, setGeneratedAd] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Analyzing, 3: Result
  const [analysisSteps, setAnalysisSteps] = useState([]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        analyzeImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        analyzeImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const updateStep = (stepName, status) => {
    setAnalysisSteps(prev => {
      const existing = prev.find(s => s.name === stepName);
      if (existing) {
        return prev.map(s => s.name === stepName ? { ...s, status } : s);
      }
      return [...prev, { name: stepName, status }];
    });
  };

  const analyzeImage = async (imageDataUrl) => {
    setStep(2);
    setIsAnalyzing(true);
    setError(null);
    setAnalysisSteps([]);

    try {
      // Extrair base64 da data URL
      const base64Data = imageDataUrl.split(',')[1];

      // Atualizar passos visuais
      updateStep("Identificando produto", "active");
      
      // Chamar API real
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/ia/analyze-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_base64: base64Data,
          extra_context: ""
        })
      });
      if (!resp.ok) throw new Error(`Erro ao analisar imagem (${resp.status})`);
      const response = { data: await resp.json() };

      updateStep("Identificando produto", "done");
      updateStep("Analisando características", "active");
      await new Promise(res => setTimeout(res, 300));
      
      updateStep("Analisando características", "done");
      updateStep("Gerando título otimizado", "active");
      await new Promise(res => setTimeout(res, 300));
      
      updateStep("Gerando título otimizado", "done");
      updateStep("Criando descrição", "active");
      await new Promise(res => setTimeout(res, 300));
      
      updateStep("Criando descrição", "done");
      updateStep("Sugerindo preço", "active");
      await new Promise(res => setTimeout(res, 300));
      
      updateStep("Sugerindo preço", "done");
      updateStep("Finalizando anúncio", "done");

      // Mapear resposta para formato esperado
      const result = {
        title: response.data.title || "Produto Analisado",
        category: response.data.category || "A definir",
        suggestedPrice: response.data.suggested_price || "A consultar",
        description: response.data.description || "",
        bullets: response.data.bullets || [],
        tags: response.data.tags || [],
        condition: response.data.condition || "Novo",
        stock: 10,
        weight: response.data.weight || "N/A",
        dimensions: response.data.dimensions || "N/A",
        detectedFeatures: response.data.detected_features || [],
        brand: response.data.brand || "N/A",
        model: response.data.model || "N/A",
        color: response.data.color || "N/A",
        material: response.data.material || "N/A",
        targetAudience: response.data.target_audience || "Geral",
        seoKeywords: response.data.seo_keywords || []
      };

      setGeneratedAd(result);
      setIsAnalyzing(false);
      setStep(3);

    } catch (err) {
      console.error("Erro ao analisar imagem:", err);
      setError(err.response?.data?.detail || "Erro ao analisar imagem. Tente novamente.");
      setIsAnalyzing(false);
      setStep(1);
    }
  };

  const resetForm = () => {
    setUploadedImage(null);
    setImageFile(null);
    setGeneratedAd(null);
    setError(null);
    setStep(1);
    setAnalysisSteps([]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const publishAd = () => {
    alert("🎉 Anúncio publicado com sucesso nos marketplaces selecionados!");
  };

  return (
    <div className="p-4 lg:p-6 bg-gray-950 overflow-auto h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase">
            IA Smart
          </span>
          <h1 className="text-xl lg:text-2xl font-bold text-white">
            Gerador de Anúncios por Imagem
          </h1>
        </div>
        <p className="text-xs lg:text-sm text-gray-400">
          Arraste uma foto do produto e nossa IA cria o anúncio completo automaticamente
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 text-sm">{error}</span>
          <Button size="sm" variant="ghost" onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Step 1: Upload Image */}
      {step === 1 && (
        <div className="max-w-3xl mx-auto">
          <Card className="bg-gray-900/50 border-gray-700 p-6">
            {/* Upload Area */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-purple-500/50 rounded-xl p-8 text-center hover:border-purple-500 transition-all cursor-pointer bg-purple-500/5"
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-3">
                    <Upload className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Arraste sua imagem aqui
                  </h3>
                  <p className="text-gray-400 text-sm mb-3">
                    ou clique para selecionar um arquivo
                  </p>
                  <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
                    <span>✓ JPG, PNG, WebP</span>
                    <span>•</span>
                    <span>✓ Até 10MB</span>
                    <span>•</span>
                    <span>✓ Alta qualidade</span>
                  </div>
                </div>
              </label>
            </div>

            {/* Info Cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <ImageIcon className="w-4 h-4 text-blue-400" />
                  <h4 className="font-semibold text-white text-sm">1. Envie a Foto</h4>
                </div>
                <p className="text-xs text-gray-400">Arraste uma foto clara do produto</p>
              </div>

              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <h4 className="font-semibold text-white text-sm">2. IA Analisa</h4>
                </div>
                <p className="text-xs text-gray-400">GPT-4o Vision identifica o produto</p>
              </div>

              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-green-400" />
                  <h4 className="font-semibold text-white text-sm">3. Anúncio Pronto</h4>
                </div>
                <p className="text-xs text-gray-400">Título, descrição e preço gerados</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 2 && (
        <div className="max-w-3xl mx-auto">
          <Card className="bg-gray-900/50 border-gray-700 p-6">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-4 relative">
                <img 
                  src={uploadedImage} 
                  alt="Produto" 
                  className="w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <Loader className="w-8 h-8 text-purple-400 animate-spin" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-white mb-1">
                Analisando sua imagem...
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Nossa IA está identificando o produto e criando o anúncio
              </p>

              <div className="space-y-2 max-w-sm mx-auto">
                {[
                  "Identificando produto",
                  "Analisando características",
                  "Gerando título otimizado",
                  "Criando descrição",
                  "Sugerindo preço",
                  "Finalizando anúncio"
                ].map((label, idx) => {
                  const stepData = analysisSteps.find(s => s.name === label);
                  const status = stepData?.status || "pending";
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                        status === "done" ? "bg-green-500/10 text-green-400" :
                        status === "active" ? "bg-purple-500/10 text-purple-400 animate-pulse" :
                        "bg-gray-800/50 text-gray-500"
                      }`}
                    >
                      {status === "done" ? (
                        <Check className="w-4 h-4" />
                      ) : status === "active" ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-600" />
                      )}
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && generatedAd && (
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Preview */}
            <div className="lg:col-span-1">
              <Card className="bg-gray-900/50 border-gray-700 p-4 sticky top-4">
                <div className="mb-3">
                  <img 
                    src={uploadedImage} 
                    alt="Produto" 
                    className="w-full rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Button onClick={resetForm} variant="outline" className="w-full border-gray-700 h-8 text-sm">
                    <X className="w-3 h-3 mr-1" />
                    Nova Imagem
                  </Button>
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 h-8 text-sm">
                    <Edit className="w-3 h-3 mr-1" />
                    Editar Anúncio
                  </Button>
                  <Button 
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 h-8 text-sm" 
                    onClick={publishAd}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Publicar Agora
                  </Button>
                </div>

                <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-xs font-semibold">
                    ✓ Gerado com GPT-4o Vision
                  </p>
                </div>
              </Card>
            </div>

            {/* Generated Content */}
            <div className="lg:col-span-2 space-y-3">
              {/* Title */}
              <Card className="bg-gray-900/50 border-gray-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white text-sm">Título do Anúncio</h3>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(generatedAd.title)} className="h-6">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-base font-bold text-white">{generatedAd.title}</p>
              </Card>

              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-gray-900/50 border-gray-700 p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Categoria</p>
                  <p className="text-white font-medium text-sm">{generatedAd.category}</p>
                </Card>
                <Card className="bg-gray-900/50 border-gray-700 p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Preço Sugerido</p>
                  <p className="text-xl font-bold text-green-400">{generatedAd.suggestedPrice}</p>
                </Card>
              </div>

              {/* Description */}
              <Card className="bg-gray-900/50 border-gray-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white text-sm">Descrição</h3>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(generatedAd.description)} className="h-6">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{generatedAd.description}</p>
              </Card>

              {/* Bullets */}
              {generatedAd.bullets && generatedAd.bullets.length > 0 && (
                <Card className="bg-gray-900/50 border-gray-700 p-3">
                  <h3 className="font-semibold text-white text-sm mb-2">Características</h3>
                  <ul className="space-y-1">
                    {generatedAd.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-xs">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Detected Features + Details */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-gray-900/50 border-gray-700 p-3">
                  <h3 className="font-semibold text-white text-sm mb-2">Detalhes</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Condição:</span>
                      <span className="text-white font-medium">{generatedAd.condition}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Marca:</span>
                      <span className="text-white font-medium">{generatedAd.brand}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Cor:</span>
                      <span className="text-white font-medium">{generatedAd.color}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Material:</span>
                      <span className="text-white font-medium">{generatedAd.material}</span>
                    </div>
                  </div>
                </Card>

                <Card className="bg-gray-900/50 border-gray-700 p-3">
                  <h3 className="font-semibold text-white text-sm mb-2">IA Detectou</h3>
                  <div className="space-y-1">
                    {generatedAd.detectedFeatures && generatedAd.detectedFeatures.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-1">
                        <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-xs">{feature}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Tags */}
              {generatedAd.tags && generatedAd.tags.length > 0 && (
                <Card className="bg-gray-900/50 border-gray-700 p-3">
                  <h3 className="font-semibold text-white text-sm mb-2">Tags Sugeridas</h3>
                  <div className="flex flex-wrap gap-1">
                    {generatedAd.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IAImageAds;
